import { type Api } from "api-client";
import { sort } from "fast-sort";
import { comparer, makeAutoObservable, observable } from "mobx";
import { computedFn } from "mobx-utils";
import {
  assert,
  canWorkoutFormatHaveSupersets,
  mapBy,
  orderUtilsMutable,
  WORKOUT_GRID_MAX_SELECTION_COUNT,
} from "utils";

type WorkoutItem = Api.GetProgram.Output.WorkoutItem;

export type WorkoutGridSelectionDomain = "workout" | "workoutItem";

export interface WorkoutGridSelectionStatus {
  domain: WorkoutGridSelectionDomain;
  count: number;
}

export interface WorkoutGridWorkout {
  id: string;
  workoutItems: WorkoutItem[];
}

export type WorkoutItemOperation =
  | Api.BulkCopyWorkoutItem.Output.Operation
  | Api.BulkDeleteWorkoutItem.Output.Operation
  | Api.MoveWorkoutItemToWorkout.Output.Operation;

interface WorkoutGridStoreInit {
  getWorkouts: () => WorkoutGridWorkout[];
  isWorkoutCardSelectable: (workoutCardId: string) => boolean;
  isWorkoutCardCopyable: (workoutCardId: string) => boolean;
  isWorkoutCardTask: (workoutCardId: string) => boolean;
  isWorkoutCardDeletable: (workoutCardId: string) => boolean;
  isWorkoutEditable: (workoutId: string) => boolean;
}

interface WorkoutItemMoveTargetInput {
  workoutItemId: string;
  targetWorkoutItemId: string;
}

interface WorkoutItemMoveParentInput {
  workoutItemId: string;
  parentWorkoutItemId: string | null;
  destinationWorkoutId: string;
}

interface WorkoutItemCopyParentInput {
  workoutItemIds: string[];
  parentWorkoutItemId: string | null;
  destinationWorkoutId: string;
}

interface WorkoutItemCopyTargetInput extends WorkoutItemCopyParentInput {
  order: number;
}

interface MoveWorkoutItemInput extends WorkoutItemMoveParentInput {
  order: number;
}

interface WorkoutItemUpdatePatch {
  id: string;
  workoutId?: string;
  parentId?: string | null;
  order?: number;
}

interface MoveWorkoutItemTreeToWorkoutInput {
  workoutItemId: string;
  destinationWorkoutId: string;
}

export class WorkoutGridStore {
  _init: WorkoutGridStoreInit;
  workoutCardSelection = observable.map<string, true>();
  workoutItemSelection = observable.map<string, true>();

  constructor(init: WorkoutGridStoreInit) {
    this._init = init;
    makeAutoObservable(this, { _init: false });
  }

  get workoutsById() {
    return mapBy(this._init.getWorkouts(), (workout) => {
      return workout.id;
    });
  }

  get workoutItemsById() {
    return mapBy(
      this._init.getWorkouts().flatMap((workout) => {
        return workout.workoutItems;
      }),
      (workoutItem) => {
        return workoutItem.id;
      },
    );
  }

  get rootWorkoutItemsByWorkoutId() {
    const byWorkoutId: Record<string, WorkoutItem[]> = {};

    for (const workout of this._init.getWorkouts()) {
      byWorkoutId[workout.id] = sort(
        workout.workoutItems.filter((workoutItem) => {
          return workoutItem.parentId === null;
        }),
      ).asc("order");
    }

    return byWorkoutId;
  }

  get workoutItemsByParentId() {
    const byParentId: Record<string, WorkoutItem[]> = {};

    for (const workoutItem of Object.values(this.workoutItemsById)) {
      if (workoutItem.parentId === null) {
        continue;
      }

      let existing = byParentId[workoutItem.parentId];

      if (!existing) {
        existing = [];
        byParentId[workoutItem.parentId] = existing;
      }

      existing.push(workoutItem);
    }

    for (const parentId of Object.keys(byParentId)) {
      byParentId[parentId] = sort(byParentId[parentId]).asc("order");
    }

    return byParentId;
  }

  getWorkoutItem = computedFn((workoutItemId: string): WorkoutItem => {
    return this.workoutItemsById[workoutItemId];
  });

  getWorkoutItemWorkoutItems = computedFn(
    (workoutItemId: string): WorkoutItem[] => {
      return this.workoutItemsByParentId[workoutItemId] ?? [];
    },
    { equals: comparer.shallow },
  );

  getRootWorkoutItems = computedFn(
    (workoutId: string): WorkoutItem[] => {
      return this.rootWorkoutItemsByWorkoutId[workoutId] ?? [];
    },
    { equals: comparer.shallow },
  );

  get selectedWorkoutCardIds(): string[] {
    return Array.from(this.workoutCardSelection.keys()).filter((id) => {
      return this.isWorkoutCardSelectable(id);
    });
  }

  get selectedWorkoutItemIds(): string[] {
    const existingIds = Array.from(this.workoutItemSelection.keys()).filter(
      (id) => {
        return Boolean(this.workoutItemsById[id]);
      },
    );

    return existingIds.filter((id) => {
      return !existingIds.some((otherId) => {
        return otherId !== id && this.isWorkoutItemDescendantOf(id, otherId);
      });
    });
  }

  get selectionStatus(): WorkoutGridSelectionStatus | null {
    const workoutCardCount = this.selectedWorkoutCardIds.length;

    if (workoutCardCount > 0) {
      return { domain: "workout", count: workoutCardCount };
    }

    const workoutItemCount = this.selectedWorkoutItemIds.length;

    if (workoutItemCount > 0) {
      return { domain: "workoutItem", count: workoutItemCount };
    }

    return null;
  }

  hasSelection = (): boolean => {
    return this.selectionStatus !== null;
  };

  getMovedWorkoutCardIds = (workoutCardId: string): string[] => {
    return this.isWorkoutCardSelected(workoutCardId)
      ? this.selectedWorkoutCardIds
      : [workoutCardId];
  };

  get copyableSelectedWorkoutCardIds(): string[] {
    return this.selectedWorkoutCardIds.filter((workoutCardId) => {
      return this._init.isWorkoutCardCopyable(workoutCardId);
    });
  }

  get copyableSelectedTaskIds(): string[] {
    return this.copyableSelectedWorkoutCardIds.filter((workoutCardId) => {
      return this.isWorkoutCardTask(workoutCardId);
    });
  }

  get canCopySelection(): boolean {
    return this.copyableSelectedWorkoutCardIds.length > 0;
  }

  get canDeleteSelection(): boolean {
    return this.selectedWorkoutCardIds.every((workoutCardId) => {
      return this._init.isWorkoutCardDeletable(workoutCardId);
    });
  }

  clearSelection(): void {
    this.workoutCardSelection.clear();
    this.workoutItemSelection.clear();
  }

  isWorkoutCardSelectable(workoutCardId: string): boolean {
    return this._init.isWorkoutCardSelectable(workoutCardId);
  }

  isWorkoutCardTask(workoutCardId: string): boolean {
    return this._init.isWorkoutCardTask(workoutCardId);
  }

  isWorkoutEditable(workoutId: string): boolean {
    return this._init.isWorkoutEditable(workoutId);
  }

  selectWorkoutCard(workoutCardId: string): void {
    assert(
      this.isWorkoutCardSelectable(workoutCardId),
      `Workout card ${workoutCardId} is not selectable`,
    );

    if (
      this.selectedWorkoutCardIds.length >= WORKOUT_GRID_MAX_SELECTION_COUNT
    ) {
      return;
    }

    this.workoutItemSelection.clear();
    this.workoutCardSelection.set(workoutCardId, true);
  }

  setWorkoutCardSelection(workoutCardIds: string[]): void {
    const selectedIds = new Set(workoutCardIds);
    this.workoutItemSelection.clear();

    for (const workoutCardId of Array.from(this.workoutCardSelection.keys())) {
      if (!selectedIds.has(workoutCardId)) {
        this.workoutCardSelection.delete(workoutCardId);
      }
    }

    for (const workoutCardId of selectedIds) {
      this.workoutCardSelection.set(workoutCardId, true);
    }
  }

  toggleWorkoutCardSelection(workoutCardId: string): void {
    if (this.isWorkoutCardSelected(workoutCardId)) {
      this.workoutCardSelection.delete(workoutCardId);
      return;
    }

    this.selectWorkoutCard(workoutCardId);
  }

  toggleWorkoutItemSelection(workoutItemId: string): void {
    if (this.isWorkoutItemSelectionRoot(workoutItemId)) {
      this.workoutItemSelection.delete(workoutItemId);
      return;
    }

    if (
      this.hasSelectedWorkoutItemAncestor(workoutItemId) ||
      this.selectedWorkoutItemIds.length >= WORKOUT_GRID_MAX_SELECTION_COUNT
    ) {
      return;
    }

    for (const selectedWorkoutItemId of this.selectedWorkoutItemIds) {
      if (
        this.isWorkoutItemDescendantOf(selectedWorkoutItemId, workoutItemId)
      ) {
        this.workoutItemSelection.delete(selectedWorkoutItemId);
      }
    }

    this.workoutCardSelection.clear();
    this.workoutItemSelection.set(workoutItemId, true);
  }

  isWorkoutCardSelected(workoutCardId: string): boolean {
    return this.workoutCardSelection.has(workoutCardId);
  }

  isWorkoutItemSelectionRoot(workoutItemId: string): boolean {
    return this.workoutItemSelection.has(workoutItemId);
  }

  private hasSelectedWorkoutItemAncestor(workoutItemId: string): boolean {
    return this.selectedWorkoutItemIds.some((selectedWorkoutItemId) => {
      return this.isWorkoutItemDescendantOf(
        workoutItemId,
        selectedWorkoutItemId,
      );
    });
  }

  canMoveWorkoutItemToTarget(input: WorkoutItemMoveTargetInput): boolean {
    const target = this.getWorkoutItem(input.targetWorkoutItemId);

    if (!target || input.workoutItemId === target.id) {
      return false;
    }

    return this.canMoveWorkoutItemToParent({
      workoutItemId: input.workoutItemId,
      parentWorkoutItemId: target.parentId,
      destinationWorkoutId: target.workoutId,
    });
  }

  canMoveWorkoutItemToParent(input: WorkoutItemMoveParentInput): boolean {
    const item = this.getWorkoutItem(input.workoutItemId);
    const destinationParent = input.parentWorkoutItemId
      ? this.getWorkoutItem(input.parentWorkoutItemId)
      : null;

    if (!item) {
      return false;
    }

    if (input.parentWorkoutItemId && !destinationParent) {
      return false;
    }

    if (destinationParent && destinationParent.type !== "GROUP") {
      return false;
    }

    if (item.id === input.parentWorkoutItemId) {
      return false;
    }

    if (
      input.parentWorkoutItemId &&
      this.isWorkoutItemDescendantOf(input.parentWorkoutItemId, item.id)
    ) {
      return false;
    }

    if (this.isSectionWorkoutItem(item) && input.parentWorkoutItemId !== null) {
      return false;
    }

    if (
      this.isSupersetWorkoutItem(item) &&
      destinationParent &&
      (this.isSupersetWorkoutItem(destinationParent) ||
        !canWorkoutFormatHaveSupersets(destinationParent.format))
    ) {
      return false;
    }

    return this.isWorkoutEditable(input.destinationWorkoutId);
  }

  canCopyWorkoutItemsToParent(input: WorkoutItemCopyParentInput): boolean {
    if (input.workoutItemIds.length === 0) {
      return false;
    }

    const destinationParent = input.parentWorkoutItemId
      ? this.getWorkoutItem(input.parentWorkoutItemId)
      : null;

    if (!this.isWorkoutEditable(input.destinationWorkoutId)) {
      return false;
    }

    if (input.parentWorkoutItemId && !destinationParent) {
      return false;
    }

    if (
      destinationParent &&
      (destinationParent.type !== "GROUP" ||
        destinationParent.workoutId !== input.destinationWorkoutId)
    ) {
      return false;
    }

    const areCopiedItemsOnGrid = input.workoutItemIds.every((workoutItemId) => {
      return Boolean(this.workoutItemsById[workoutItemId]);
    });

    if (!areCopiedItemsOnGrid) {
      return true;
    }

    if (
      input.parentWorkoutItemId &&
      input.workoutItemIds.includes(input.parentWorkoutItemId)
    ) {
      return false;
    }

    return input.workoutItemIds.every((workoutItemId) => {
      return this.canMoveWorkoutItemToParent({
        workoutItemId: workoutItemId,
        parentWorkoutItemId: input.parentWorkoutItemId,
        destinationWorkoutId: input.destinationWorkoutId,
      });
    });
  }

  canCopyWorkoutItemsToTarget(input: WorkoutItemCopyTargetInput): boolean {
    if (!this.canCopyWorkoutItemsToParent(input)) {
      return false;
    }

    const destinationItemCount = this.getWorkoutItemGroup(
      input.destinationWorkoutId,
      input.parentWorkoutItemId,
    ).length;

    return input.order >= 0 && input.order <= destinationItemCount;
  }

  moveWorkoutItem(input: MoveWorkoutItemInput): void {
    assert(
      this.canMoveWorkoutItemToParent(input),
      "Cannot move workout item to parent",
    );

    const item = this.getWorkoutItem(input.workoutItemId);
    const destinationWorkout = this.workoutsById[input.destinationWorkoutId];
    assert(item, "Workout item not found");
    assert(destinationWorkout, "Destination workout not found");

    const sourceWorkout = this.workoutsById[item.workoutId];
    assert(sourceWorkout, "Source workout not found");

    const destinationParentId = input.parentWorkoutItemId;
    const sourceGroup = this.getWorkoutItemGroup(
      sourceWorkout.id,
      item.parentId,
    );
    const isSameGroup =
      sourceWorkout.id === destinationWorkout.id &&
      item.parentId === destinationParentId;
    const destinationGroup = isSameGroup
      ? sourceGroup
      : this.getWorkoutItemGroup(destinationWorkout.id, destinationParentId);
    const maxOrder = isSameGroup
      ? Math.max(sourceGroup.length - 1, 0)
      : destinationGroup.length;
    assert(input.order >= 0 && input.order <= maxOrder, "Invalid move order");

    orderUtilsMutable.move(
      item,
      sourceGroup,
      destinationGroup,
      input.order,
      destinationParentId,
      "parentId",
    );

    if (sourceWorkout.id === destinationWorkout.id) {
      return;
    }

    this.moveWorkoutItemTreeToWorkout({
      workoutItemId: input.workoutItemId,
      destinationWorkoutId: destinationWorkout.id,
    });
  }

  applyWorkoutItemOperations(operations: WorkoutItemOperation[]): void {
    const deletedWorkoutItemIds = new Set<string>();

    operations.forEach((operation) => {
      if (operation.operation === "create") {
        this.addWorkoutItem(operation.data as WorkoutItem);
        return;
      }

      if (operation.operation === "update") {
        this.applyWorkoutItemUpdate(operation.data);
        return;
      }

      deletedWorkoutItemIds.add(operation.data);
    });

    if (deletedWorkoutItemIds.size === 0) {
      return;
    }

    this._init.getWorkouts().forEach((workout) => {
      if (
        !workout.workoutItems.some((workoutItem) => {
          return deletedWorkoutItemIds.has(workoutItem.id);
        })
      ) {
        return;
      }

      workout.workoutItems = workout.workoutItems.filter((workoutItem) => {
        return !deletedWorkoutItemIds.has(workoutItem.id);
      });
    });
  }

  private addWorkoutItem(workoutItem: WorkoutItem): void {
    const workout = this.workoutsById[workoutItem.workoutId];
    assert(workout, `Workout ${workoutItem.workoutId} not found`);

    const alreadyExists = workout.workoutItems.some((existingWorkoutItem) => {
      return existingWorkoutItem.id === workoutItem.id;
    });

    if (alreadyExists) {
      return;
    }

    workout.workoutItems.push(workoutItem);
  }

  private applyWorkoutItemUpdate(data: WorkoutItemUpdatePatch): void {
    const item = this.getWorkoutItem(data.id);
    assert(item, `Workout item ${data.id} not found`);

    if (data.workoutId && data.workoutId !== item.workoutId) {
      const sourceWorkout = this.workoutsById[item.workoutId];
      const destinationWorkout = this.workoutsById[data.workoutId];
      assert(sourceWorkout, `Workout ${item.workoutId} not found`);
      assert(destinationWorkout, `Workout ${data.workoutId} not found`);
      sourceWorkout.workoutItems = sourceWorkout.workoutItems.filter(
        (workoutItem) => {
          return workoutItem.id !== item.id;
        },
      );
      destinationWorkout.workoutItems.push(item);
      item.workoutId = data.workoutId;
    }

    if (Object.hasOwn(data, "parentId")) {
      item.parentId = data.parentId ?? null;
    }

    if (data.order !== undefined) {
      item.order = data.order;
    }
  }

  private getWorkoutItemGroup(
    workoutId: string,
    parentWorkoutItemId: string | null,
  ): WorkoutItem[] {
    const workout = this.workoutsById[workoutId];
    assert(workout, "Workout not found");

    return workout.workoutItems.filter((workoutItem) => {
      return workoutItem.parentId === parentWorkoutItemId;
    });
  }

  private moveWorkoutItemTreeToWorkout(
    input: MoveWorkoutItemTreeToWorkoutInput,
  ): void {
    const item = this.getWorkoutItem(input.workoutItemId);
    assert(item, "Workout item not found");

    const sourceWorkout = this.workoutsById[item.workoutId];
    const destinationWorkout = this.workoutsById[input.destinationWorkoutId];
    assert(sourceWorkout, "Source workout not found");
    assert(destinationWorkout, "Destination workout not found");

    const movedItems = this.collectWorkoutItemSubtree(input.workoutItemId);
    const movedItemIds = new Set(
      movedItems.map((workoutItem) => {
        return workoutItem.id;
      }),
    );

    sourceWorkout.workoutItems = sourceWorkout.workoutItems.filter(
      (workoutItem) => {
        return !movedItemIds.has(workoutItem.id);
      },
    );

    for (const movedItem of movedItems) {
      movedItem.workoutId = destinationWorkout.id;
      destinationWorkout.workoutItems.push(movedItem);
    }
  }

  private isSectionWorkoutItem(workoutItem: WorkoutItem): boolean {
    return (
      workoutItem.type === "GROUP" && workoutItem.groupType !== "SUPER_SET"
    );
  }

  private isSupersetWorkoutItem(workoutItem: WorkoutItem): boolean {
    return (
      workoutItem.type === "GROUP" && workoutItem.groupType === "SUPER_SET"
    );
  }

  private isWorkoutItemDescendantOf(
    workoutItemId: string,
    ancestorWorkoutItemId: string,
  ): boolean {
    const workoutItem = this.getWorkoutItem(workoutItemId);

    if (!workoutItem) {
      return false;
    }

    let currentParentId = workoutItem.parentId;

    while (currentParentId !== null) {
      if (currentParentId === ancestorWorkoutItemId) {
        return true;
      }

      const parent = this.getWorkoutItem(currentParentId);

      if (!parent) {
        return false;
      }

      currentParentId = parent.parentId;
    }

    return false;
  }

  private collectWorkoutItemSubtree(workoutItemId: string): WorkoutItem[] {
    const workoutItem = this.getWorkoutItem(workoutItemId);

    if (!workoutItem) {
      return [];
    }

    const items = [workoutItem];
    const children = this.getWorkoutItemWorkoutItems(workoutItemId);

    for (const child of children) {
      items.push(...this.collectWorkoutItemSubtree(child.id));
    }

    return items;
  }
}
